import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MessageCircle, Trash2, Plus } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, query, orderBy, updateDoc, doc, deleteDoc, addDoc } from 'firebase/firestore';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  consulting: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-orange-100 text-orange-800',
  high: 'bg-red-100 text-red-800'
};

const STATUS_LABELS = {
  new: '신규',
  contacted: '연락완료',
  consulting: '상담중',
  completed: '완료',
  rejected: '거절'
};

const PRIORITY_LABELS = {
  low: '낮음',
  medium: '보통',
  high: '높음'
};

// Helper function to safely format dates
  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('ko-KR');
    } catch (error) {
      console.warn('Date formatting error:', error);
      return '-';
    }
  };

  // Helper function to safely format datetime
  const formatDateTime = (dateString) => {
    try {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('ko-KR');
    } catch (error) {
      console.warn('DateTime formatting error:', error);
      return '-';
    }
  };

  export default function AdminContacts() {
  const navigate = useNavigate();
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [notes, setNotes] = useState('');
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    construction_type: '',
    budget_range: '',
    location: '',
    preferred_date: '',
    message: '',
    priority: 'medium'
  });

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  // 문의사항 목록 조회
  const fetchContacts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if db is properly initialized
      if (!db) {
        throw new Error('Firebase Firestore is not initialized');
      }

      // Fetch all contacts and filter client-side to avoid index requirement
      const contactsQuery = query(collection(db, 'contacts'), orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(contactsQuery);
      const allContactsData = [];
      querySnapshot.forEach((doc) => {
        // Ensure doc.data() exists and has required fields
        const data = doc.data();
        if (data) {
          allContactsData.push({ 
            id: doc.id, 
            ...data,
            created_at: data.created_at ? data.created_at : new Date().toISOString(),
            status: data.status ? data.status : 'new',
            priority: data.priority ? data.priority : 'medium'
          });
        }
      });

      // Apply filters client-side
      let filteredContacts = allContactsData;
      if (filterStatus !== 'all') {
        filteredContacts = allContactsData.filter(contact => contact.status === filterStatus);
      }
      
      if (filterPriority !== 'all') {
        filteredContacts = filteredContacts.filter(contact => contact.priority === filterPriority);
      }

      setContacts(filteredContacts);
    } catch (err) {
      console.warn('Contacts fetch failed:', err);
      console.error('Full error details:', err);
      setContacts([]);
      setError('문의사항을 불러오는 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterPriority]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // 상태 업데이트
  const updateContactStatus = async (id, status, newNotes) => {
    try {
      setIsUpdating(true);
      const contactDoc = doc(db, 'contacts', id);
      await updateDoc(contactDoc, {
        status,
        notes: newNotes || null,
        updated_at: new Date().toISOString()
      });

      // 성공 시 목록 새로고침
      fetchContacts();
    } catch (error) {
      console.error('Contact status update failed:', error);
      alert('문의사항 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  // 문의사항 삭제
  const deleteContact = async (id) => {
    try {
      setIsDeleting(true);
      const contactDoc = doc(db, 'contacts', id);
      await deleteDoc(contactDoc);

      // 성공 시 목록 새로고침
      fetchContacts();
    } catch (error) {
      console.error('Contact delete failed:', error);
      alert('문의사항 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-red-600">문의사항을 불러오는 중 오류가 발생했습니다.</p>
      </div>
    );
  }

  const openContactModal = (contact) => {
    setSelectedContact(contact);
    setNotes(contact.notes || '');
  };

  const closeModal = () => {
    setSelectedContact(null);
    setNotes('');
  };

  const handleUpdateContactStatus = async (status) => {
    if (selectedContact) {
      await updateContactStatus(selectedContact.id, status, notes);
      closeModal();
    }
  };

  const handleDeleteContact = async () => {
    if (selectedContact && window.confirm('정말로 이 문의사항을 삭제하시겠습니까?')) {
      await deleteContact(selectedContact.id);
      closeModal();
    }
  };

  // Ensure contacts is an array before calculating stats
  const safeContacts = Array.isArray(contacts) ? contacts : [];
  
  const stats = {
    total: safeContacts.length || 0,
    new: safeContacts.filter(c => c.status === 'new').length || 0,
    consulting: safeContacts.filter(c => c.status === 'consulting').length || 0,
    completed: safeContacts.filter(c => c.status === 'completed').length || 0
  };

  const handleAddConsultation = () => {
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setNewContact({
      name: '',
      company: '',
      phone: '',
      email: '',
      construction_type: '',
      budget_range: '',
      location: '',
      preferred_date: '',
      message: '',
      priority: 'medium'
    });
  };

  const handleAddContactSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsAdding(true);
      
      // Validate required fields
      if (!newContact.name || !newContact.phone || !newContact.email || 
          !newContact.construction_type || !newContact.budget_range || !newContact.location) {
        alert('모든 필수 항목(*)을 입력해주세요.');
        setIsAdding(false);
        return;
      }
      
      // Add created_at timestamp
      const contactData = {
        ...newContact,
        created_at: new Date().toISOString(),
        status: 'new',
        updated_at: new Date().toISOString()
      };
      
      // Check if db is properly initialized
      if (!db) {
        throw new Error('Firebase Firestore is not initialized');
      }
      
      await addDoc(collection(db, 'contacts'), contactData);
      
      // Refresh contacts list
      fetchContacts();
      
      // Close modal and reset form
      closeAddModal();
      
      alert('상담 신청이 성공적으로 등록되었습니다.');
    } catch (error) {
      console.error('Contact add failed:', error);
      console.error('Full error details:', error);
      alert('상담 신청 등록 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">고객 문의 관리</h1>
            <p className="text-sm text-gray-600">고객 문의사항을 확인하고 관리합니다</p>
          </div>
          <button
            onClick={handleAddConsultation}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            상담신청 추가
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">전체 문의</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <MessageCircle className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">신규 문의</p>
              <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-blue-600">N</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">상담 중</p>
              <p className="text-2xl font-bold text-purple-600">{stats.consulting}</p>
            </div>
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-purple-600">C</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">완료</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-green-600">✓</span>
            </div>
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체</option>
              <option value="new">신규</option>
              <option value="contacted">연락완료</option>
              <option value="consulting">상담중</option>
              <option value="completed">완료</option>
              <option value="rejected">거절</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">우선순위</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체</option>
              <option value="high">높음</option>
              <option value="medium">보통</option>
              <option value="low">낮음</option>
            </select>
          </div>
        </div>
      </div>

      {/* 문의 목록 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {safeContacts && safeContacts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">고객명</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">연락처</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">인테리어 유형</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">예산</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">위치</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">문의일</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {safeContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.construction_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.budget_range}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[contact.status]}`}>
                          {STATUS_LABELS[contact.status]}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[contact.priority]}`}>
                          {PRIORITY_LABELS[contact.priority]}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(contact.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openContactModal(contact)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        보기
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">문의사항이 없습니다</p>
          </div>
        )}
      </div>

      {/* 상세 모달 */}
      {selectedContact && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">문의 상세 정보</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* 고객 정보 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                    <p className="text-gray-900">{selectedContact.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">회사명</label>
                    <p className="text-gray-900">{selectedContact.company || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
                    <p className="text-gray-900">{selectedContact.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                    <p className="text-gray-900">{selectedContact.email}</p>
                  </div>
                </div>

                {/* 프로젝트 정보 */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">프로젝트 정보</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">인테리어 유형</label>
                      <p className="text-gray-900">{selectedContact.construction_type}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예산 범위</label>
                      <p className="text-gray-900">{selectedContact.budget_range}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">위치</label>
                      <p className="text-gray-900">{selectedContact.location}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">희망 상담일</label>
                      <p className="text-gray-900">{selectedContact.preferred_date ? formatDate(selectedContact.preferred_date) : '-'}</p>
                    </div>
                  </div>
                </div>

                {/* 문의 내용 */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">문의 내용</h4>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>
                </div>

                {/* 상태 및 우선순위 */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">관리 정보</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">현재 상태</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedContact.status]}`}>
                        {STATUS_LABELS[selectedContact.status]}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">우선순위</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[selectedContact.priority]}`}>
                        {PRIORITY_LABELS[selectedContact.priority]}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">문의일</label>
                    <p className="text-gray-900">{formatDateTime(selectedContact.created_at)}</p>
                  </div>
                </div>

                {/* 내부 메모 */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">내부 메모</h4>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="내부 메모를 입력하세요..."
                  />
                </div>

                {/* 액션 버튼 */}
                <div className="border-t pt-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => handleUpdateContactStatus('contacted')}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdating ? '처리 중...' : '연락완료로 변경'}
                  </button>
                  <button
                    onClick={() => handleUpdateContactStatus('consulting')}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdating ? '처리 중...' : '상담중으로 변경'}
                  </button>
                  <button
                    onClick={() => handleUpdateContactStatus('completed')}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdating ? '처리 중...' : '완료로 변경'}
                  </button>
                  <button
                    onClick={() => handleUpdateContactStatus('rejected')}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdating ? '처리 중...' : '거절로 변경'}
                  </button>
                  <button
                    onClick={handleDeleteContact}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4 inline mr-1" />
                    {isDeleting ? '삭제 중...' : '삭제'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 상담신청 추가 모달 */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">상담 신청 추가</h3>
                <button
                  onClick={closeAddModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleAddContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객명 *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">회사명</label>
                    <input
                      type="text"
                      value={newContact.company}
                      onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">전화번호 *</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">이메일 *</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">인테리어 유형 *</label>
                    <select
                      value={newContact.construction_type}
                      onChange={(e) => setNewContact({...newContact, construction_type: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">선택하세요</option>
                      <option value="주거공간">주거공간</option>
                      <option value="상업공간">상업공간</option>
                      <option value="오피스">오피스</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예산 범위 *</label>
                    <select
                      value={newContact.budget_range}
                      onChange={(e) => setNewContact({...newContact, budget_range: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">선택하세요</option>
                      <option value="5천만원 미만">5천만원 미만</option>
                      <option value="5천만원 ~ 1억원">5천만원 ~ 1억원</option>
                      <option value="1억원 ~ 3억원">1억원 ~ 3억원</option>
                      <option value="3억원 이상">3억원 이상</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">위치 *</label>
                    <input
                      type="text"
                      value={newContact.location}
                      onChange={(e) => setNewContact({...newContact, location: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">희망 상담일</label>
                    <input
                      type="date"
                      value={newContact.preferred_date}
                      onChange={(e) => setNewContact({...newContact, preferred_date: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">우선순위</label>
                    <select
                      value={newContact.priority}
                      onChange={(e) => setNewContact({...newContact, priority: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">낮음</option>
                      <option value="medium">보통</option>
                      <option value="high">높음</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">문의 내용</label>
                    <textarea
                      value={newContact.message}
                      onChange={(e) => setNewContact({...newContact, message: e.target.value})}
                      rows={4}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="추가 요청사항이나 문의 내용을 입력해주세요..."
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                    disabled={isAdding}
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={isAdding}
                  >
                    {isAdding ? '등록 중...' : '상담 신청 등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
